import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, UserPlus, TrendingUp, MessageCircle, Phone, Mail, Search,
  Filter, Upload, Download, RefreshCw, Loader2, CheckCircle, XCircle,
  Target, Zap, Send, FileText, ExternalLink, Calendar, Trash2, Edit,
  Megaphone, ChevronRight, Clock, Star, Flame
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: 'new', label: 'New', color: 'bg-blue-500', lightColor: 'bg-blue-500/10 border-blue-500/30 text-blue-600' },
  { id: 'contacted', label: 'Contacted', color: 'bg-indigo-500', lightColor: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600' },
  { id: 'interested', label: 'Interested', color: 'bg-purple-500', lightColor: 'bg-purple-500/10 border-purple-500/30 text-purple-600' },
  { id: 'qualified', label: 'Qualified', color: 'bg-amber-500', lightColor: 'bg-amber-500/10 border-amber-500/30 text-amber-600' },
  { id: 'converted', label: 'Converted', color: 'bg-emerald-500', lightColor: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600' },
  { id: 'lost', label: 'Lost', color: 'bg-rose-500', lightColor: 'bg-rose-500/10 border-rose-500/30 text-rose-600' },
];

const SOURCES = ['facebook', 'instagram', 'google', 'website', 'referral', 'manual', 'csv', 'zapier', 'other'];

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const LeadsCRM = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pipeline'); // pipeline | list | analytics
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterSource, setFilterSource] = useState('all');

  // Dialogs
  const [showNewLead, setShowNewLead] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [activeLead, setActiveLead] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);

  const [newLead, setNewLead] = useState({
    name: '', phone: '', email: '', course_interest: '', source: 'manual', campaign: '', notes: ''
  });
  const [importFile, setImportFile] = useState(null);
  const [importSource, setImportSource] = useState('csv');
  const [importCampaign, setImportCampaign] = useState('');
  const [importSendBrochure, setImportSendBrochure] = useState(true);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkIncludeBrochure, setBulkIncludeBrochure] = useState(true);
  const [customMessage, setCustomMessage] = useState('');

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (filterStage !== 'all') params.stage = filterStage;
      if (filterSource !== 'all') params.source = filterSource;
      if (search) params.search = search;

      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: authHdr(), params }),
        axios.get(`${API}/leads/stats`, { headers: authHdr() }),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [filterStage, filterSource, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const openLead = async (leadId) => {
    try {
      const res = await axios.get(`${API}/leads/${leadId}`, { headers: authHdr() });
      setLeadDetail(res.data);
      setActiveLead(leadId);
    } catch {
      toast.error('Failed to load lead details');
    }
  };

  const createLead = async () => {
    if (!newLead.name || !newLead.phone) { toast.error('Name and phone are required'); return; }
    try {
      const res = await axios.post(`${API}/leads`, newLead, { headers: authHdr() });
      if (res.data.duplicate) toast.warning('Lead with this phone already exists');
      else toast.success('Lead created! Brochure sent via WhatsApp.');
      setShowNewLead(false);
      setNewLead({ name: '', phone: '', email: '', course_interest: '', source: 'manual', campaign: '', notes: '' });
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create lead');
    }
  };

  const updateLeadStage = async (leadId, stage) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { stage }, { headers: authHdr() });
      toast.success(`Moved to ${stage}`);
      fetchLeads();
      if (activeLead === leadId) openLead(leadId);
    } catch {
      toast.error('Failed to update stage');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { headers: authHdr() });
      toast.success('Lead deleted');
      setActiveLead(null); setLeadDetail(null);
      fetchLeads();
    } catch { toast.error('Failed to delete'); }
  };

  const resendBrochure = async (leadId) => {
    try {
      await axios.post(`${API}/leads/${leadId}/send-brochure`, {}, { headers: authHdr() });
      toast.success('Brochure resent on WhatsApp');
      if (activeLead === leadId) openLead(leadId);
    } catch { toast.error('Failed to send'); }
  };

  const sendCustomMessage = async () => {
    if (!customMessage.trim() || !activeLead) return;
    try {
      await axios.post(`${API}/leads/${activeLead}/send-message`,
        { message: customMessage }, { headers: authHdr() });
      toast.success('Message sent');
      setCustomMessage('');
      openLead(activeLead);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Send failed'); }
  };

  const uploadCSV = async () => {
    if (!importFile) { toast.error('Pick a CSV file'); return; }
    const fd = new FormData();
    fd.append('file', importFile);
    fd.append('source', importSource);
    fd.append('campaign', importCampaign);
    fd.append('send_brochure', importSendBrochure);
    try {
      const res = await axios.post(`${API}/leads/import-csv`, fd, {
        headers: { ...authHdr(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Imported ${res.data.imported}, skipped ${res.data.skipped}`);
      setShowImport(false); setImportFile(null);
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    }
  };

  const sendBulkMessage = async () => {
    if (selectedLeads.length === 0 || !bulkMessage.trim()) {
      toast.error('Select leads and enter message'); return;
    }
    try {
      const res = await axios.post(`${API}/leads/bulk-message`, {
        lead_ids: selectedLeads, message: bulkMessage, include_brochure: bulkIncludeBrochure,
      }, { headers: authHdr() });
      toast.success(`Sent to ${res.data.sent}/${res.data.total}`);
      setShowBulk(false); setBulkMessage(''); setSelectedLeads([]);
    } catch { toast.error('Bulk send failed'); }
  };

  const toggleLeadSelect = (leadId) => {
    setSelectedLeads(prev => prev.includes(leadId)
      ? prev.filter(i => i !== leadId)
      : [...prev, leadId]);
  };

  const getLeadsByStage = (stage) => leads.filter(l => l.stage === stage);

  if (loading) {
    return <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="leads-crm">
      {/* Header + actions */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Target className="w-6 h-6 text-red-500" /> Marketing CRM — Leads
          </h2>
          <p className="text-sm text-muted-foreground">
            FB/IG → WhatsApp brochure → 5-day drip → convert
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={fetchLeads} variant="outline" size="sm" data-testid="refresh-leads-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={() => setShowImport(true)} variant="outline" size="sm" data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          {selectedLeads.length > 0 && (
            <Button onClick={() => setShowBulk(true)} variant="outline" size="sm"
              className="border-orange-500 text-orange-600" data-testid="bulk-msg-btn">
              <Megaphone className="w-4 h-4 mr-2" /> Bulk Message ({selectedLeads.length})
            </Button>
          )}
          <Button onClick={() => setShowNewLead(true)} size="sm"
            className="bg-gradient-to-r from-red-500 to-orange-500" data-testid="new-lead-btn">
            <UserPlus className="w-4 h-4 mr-2" /> New Lead
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Users} label="Total Leads" value={stats.total} color="text-blue-500" />
          <StatCard icon={Flame} label="New" value={stats.by_stage?.new || 0} color="text-orange-500" />
          <StatCard icon={Star} label="Qualified" value={stats.by_stage?.qualified || 0} color="text-amber-500" />
          <StatCard icon={CheckCircle} label="Converted" value={stats.converted} color="text-emerald-500" />
          <StatCard icon={TrendingUp} label="Conv. Rate" value={`${stats.conversion_rate}%`} color="text-purple-500" />
        </div>
      )}

      {/* View switcher + filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={view} onValueChange={setView} className="flex-1 min-w-[280px]">
          <TabsList>
            <TabsTrigger value="pipeline" data-testid="view-pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="list" data-testid="view-list">List</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="view-analytics">Analytics</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search name/phone/email" value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9 w-64" data-testid="search-leads" />
        </div>
        <Select value={filterStage} onValueChange={setFilterStage}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Pipeline Kanban */}
      {view === 'pipeline' && (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {STAGES.map(stage => {
            const list = getLeadsByStage(stage.id);
            return (
              <div key={stage.id} className="bg-card rounded-lg border flex flex-col min-h-[500px]"
                data-testid={`col-${stage.id}`}>
                <div className={`px-3 py-2 border-b flex items-center justify-between ${stage.lightColor}`}>
                  <div className="font-semibold text-sm">{stage.label}</div>
                  <Badge variant="outline" className="text-xs">{list.length}</Badge>
                </div>
                <ScrollArea className="flex-1 p-2">
                  <div className="space-y-2">
                    {list.map(lead => (
                      <LeadCard key={lead.lead_id} lead={lead}
                        onClick={() => openLead(lead.lead_id)}
                        onMoveNext={(s) => updateLeadStage(lead.lead_id, s)} />
                    ))}
                    {list.length === 0 && (
                      <div className="text-xs text-muted-foreground text-center py-8">No leads</div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="p-3 text-left">
                      <Checkbox checked={selectedLeads.length === leads.length && leads.length > 0}
                        onCheckedChange={(c) => setSelectedLeads(c ? leads.map(l => l.lead_id) : [])} />
                    </th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Phone</th>
                    <th className="p-3 text-left">Course</th>
                    <th className="p-3 text-left">Source</th>
                    <th className="p-3 text-left">Stage</th>
                    <th className="p-3 text-left">Created</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => {
                    const stageConfig = STAGES.find(s => s.id === lead.stage);
                    return (
                      <tr key={lead.lead_id} className="border-t hover:bg-muted/30"
                        data-testid={`lead-row-${lead.lead_id}`}>
                        <td className="p-3">
                          <Checkbox checked={selectedLeads.includes(lead.lead_id)}
                            onCheckedChange={() => toggleLeadSelect(lead.lead_id)} />
                        </td>
                        <td className="p-3 font-medium cursor-pointer" onClick={() => openLead(lead.lead_id)}>
                          {lead.name}
                        </td>
                        <td className="p-3 font-mono text-xs">+{lead.phone}</td>
                        <td className="p-3">{lead.course_interest || '—'}</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs">{lead.source}</Badge></td>
                        <td className="p-3">
                          <Badge className={`${stageConfig?.lightColor} border text-xs`}>{stageConfig?.label}</Badge>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3 text-right">
                          <Button size="sm" variant="ghost" onClick={() => openLead(lead.lead_id)}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {leads.length === 0 && (
                    <tr><td colSpan={8} className="p-12 text-center text-muted-foreground">No leads yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analytics */}
      {view === 'analytics' && stats && <Analytics stats={stats} />}

      {/* New Lead Dialog */}
      <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>Brochure will auto-send on WhatsApp + 5-day drip schedule starts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} data-testid="new-lead-name" /></div>
            <div><Label>Phone (with country code) *</Label><Input placeholder="919876543210" value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} data-testid="new-lead-phone" /></div>
            <div><Label>Email</Label><Input value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} /></div>
            <div><Label>Course Interest</Label><Input placeholder="e.g. B.Des Animation" value={newLead.course_interest} onChange={e => setNewLead({ ...newLead, course_interest: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={newLead.source} onValueChange={v => setNewLead({ ...newLead, source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Campaign</Label><Input value={newLead.campaign} onChange={e => setNewLead({ ...newLead, campaign: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNewLead(false)}>Cancel</Button>
            <Button onClick={createLead} data-testid="submit-new-lead" className="bg-gradient-to-r from-red-500 to-orange-500">
              <Send className="w-4 h-4 mr-2" /> Create & Send Brochure
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Import Leads (CSV)</DialogTitle>
            <DialogDescription>Columns: name, phone, email, course_interest (case-insensitive)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>CSV File</Label>
              <Input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])} data-testid="csv-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={importSource} onValueChange={setImportSource}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Campaign name</Label><Input value={importCampaign} onChange={e => setImportCampaign(e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={importSendBrochure} onCheckedChange={setImportSendBrochure} id="sb" />
              <label htmlFor="sb" className="text-sm cursor-pointer">Auto-send brochure + start 5-day drip</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button onClick={uploadCSV} data-testid="submit-csv-import">
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Message Dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk WhatsApp Broadcast ({selectedLeads.length} leads)</DialogTitle>
            <DialogDescription>Only lands in WhatsApp if recipient replied in last 24 hours. Otherwise use Meta-approved templates.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea rows={5} placeholder="Your message..."
              value={bulkMessage} onChange={e => setBulkMessage(e.target.value)} data-testid="bulk-message-text" />
            <div className="flex items-center gap-2">
              <Checkbox checked={bulkIncludeBrochure} onCheckedChange={setBulkIncludeBrochure} id="bb" />
              <label htmlFor="bb" className="text-sm cursor-pointer">Include brochure link</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowBulk(false)}>Cancel</Button>
            <Button onClick={sendBulkMessage} data-testid="submit-bulk-send">
              <Send className="w-4 h-4 mr-2" /> Send Broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={!!activeLead} onOpenChange={(o) => { if (!o) { setActiveLead(null); setLeadDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {leadDetail && <LeadDetailPanel detail={leadDetail}
            onStageChange={(s) => updateLeadStage(activeLead, s)}
            onDelete={() => deleteLead(activeLead)}
            onResendBrochure={() => resendBrochure(activeLead)}
            customMessage={customMessage} setCustomMessage={setCustomMessage}
            sendCustomMessage={sendCustomMessage} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </CardContent>
  </Card>
);

const LeadCard = ({ lead, onClick, onMoveNext }) => {
  const stageIdx = STAGES.findIndex(s => s.id === lead.stage);
  const nextStage = stageIdx >= 0 && stageIdx < 4 ? STAGES[stageIdx + 1] : null;
  return (
    <div className="bg-background rounded-md border p-3 cursor-pointer hover:border-primary hover:shadow-sm transition-all"
      onClick={onClick} data-testid={`lead-card-${lead.lead_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate">{lead.name}</div>
          <div className="text-xs text-muted-foreground font-mono truncate">+{lead.phone}</div>
        </div>
        <Badge variant="outline" className="text-[10px] shrink-0">{lead.source}</Badge>
      </div>
      {lead.course_interest && (
        <div className="text-xs mt-2 text-muted-foreground truncate">
          <Star className="w-3 h-3 inline mr-1" />{lead.course_interest}
        </div>
      )}
      {(lead.tags || []).includes('needs_human') && (
        <Badge className="mt-2 bg-orange-500/10 text-orange-600 border-orange-500/30 text-[10px]">
          <Flame className="w-3 h-3 mr-1" /> Needs human
        </Badge>
      )}
      {nextStage && (
        <Button size="sm" variant="ghost" className="w-full mt-2 h-7 text-xs justify-between"
          onClick={(e) => { e.stopPropagation(); onMoveNext(nextStage.id); }}>
          Move to {nextStage.label} <ChevronRight className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
};

const LeadDetailPanel = ({ detail, onStageChange, onDelete, onResendBrochure,
                           customMessage, setCustomMessage, sendCustomMessage }) => {
  const { lead, activities, drips } = detail;
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-3">
          {lead.name}
          <Badge variant="outline">{lead.source}</Badge>
        </DialogTitle>
        <DialogDescription>
          Lead ID: {lead.lead_id} • Created {new Date(lead.created_at).toLocaleString()}
        </DialogDescription>
      </DialogHeader>

      {/* Contact info */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-muted-foreground">Phone: </span><span className="font-mono">+{lead.phone}</span></div>
        <div><span className="text-muted-foreground">Email: </span>{lead.email || '—'}</div>
        <div><span className="text-muted-foreground">Course: </span>{lead.course_interest || '—'}</div>
        <div><span className="text-muted-foreground">Campaign: </span>{lead.campaign || '—'}</div>
      </div>

      {/* Stage */}
      <div>
        <Label>Stage</Label>
        <Select value={lead.stage} onValueChange={onStageChange}>
          <SelectTrigger className="mt-1" data-testid="detail-stage-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Quick actions */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={onResendBrochure}>
          <FileText className="w-4 h-4 mr-2" /> Resend Brochure
        </Button>
        <Button size="sm" variant="outline" onClick={onDelete} className="text-rose-600 border-rose-300">
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </Button>
      </div>

      {/* Send custom message */}
      <div>
        <Label>Send WhatsApp Message</Label>
        <div className="flex gap-2 mt-1">
          <Textarea rows={2} placeholder="Type a personal message..." value={customMessage}
            onChange={e => setCustomMessage(e.target.value)} data-testid="detail-custom-msg" />
          <Button onClick={sendCustomMessage} className="self-end"><Send className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Drip progress */}
      <div>
        <Label>Drip Schedule (5-touch)</Label>
        <div className="space-y-1 mt-2">
          {drips.map(d => (
            <div key={d.drip_id} className="flex items-center justify-between text-xs border rounded px-3 py-2">
              <div>
                <span className="font-semibold">{d.step}</span>{' '}
                <span className="text-muted-foreground">— {new Date(d.scheduled_for).toLocaleString()}</span>
              </div>
              <Badge variant="outline" className={
                d.status === 'sent' ? 'text-emerald-600 border-emerald-500/30'
                : d.status === 'failed' ? 'text-rose-600 border-rose-500/30'
                : d.status === 'cancelled' ? 'text-muted-foreground'
                : 'text-blue-600 border-blue-500/30'}>
                {d.status}
              </Badge>
            </div>
          ))}
          {drips.length === 0 && <div className="text-xs text-muted-foreground">No drips scheduled</div>}
        </div>
      </div>

      {/* Activity timeline */}
      <div>
        <Label>Activity Timeline</Label>
        <ScrollArea className="h-[240px] mt-2 pr-2">
          <div className="space-y-2">
            {activities.map(a => (
              <div key={a.activity_id} className="text-xs border-l-2 border-muted pl-3 py-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">{a.activity_type}</Badge>
                  <span className="text-muted-foreground">{new Date(a.created_at).toLocaleString()}</span>
                  <span className="text-muted-foreground">• {a.actor}</span>
                </div>
                <div className="mt-1">{a.content}</div>
              </div>
            ))}
            {activities.length === 0 && <div className="text-xs text-muted-foreground">No activity yet</div>}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

const Analytics = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Leads by Source</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(stats.by_source).filter(([, v]) => v > 0).map(([src, count]) => {
            const pct = (count / Math.max(stats.total, 1)) * 100;
            return (
              <div key={src}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="capitalize">{src}</span>
                  <span className="font-semibold">{count} ({pct.toFixed(1)}%)</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 to-orange-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {stats.total === 0 && <div className="text-sm text-muted-foreground">No leads yet</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Funnel</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {stats.funnel.map(step => {
            const stageCfg = STAGES.find(s => s.id === step.stage);
            const pct = stats.total > 0 ? (step.count / stats.total) * 100 : 0;
            return (
              <div key={step.stage}>
                <div className="flex justify-between text-xs mb-1">
                  <span>{stageCfg?.label}</span>
                  <span className="font-semibold">{step.count}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${stageCfg?.color}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          <div className="pt-3 border-t mt-3 text-sm flex justify-between">
            <span>Conversion rate</span>
            <span className="font-bold text-emerald-600">{stats.conversion_rate}%</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LeadsCRM;
